import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Center,
  useToast,
  Button,
  ButtonGroup,
  Text,
  Tooltip,
  Alert,
  AlertIcon,
  Box,
  FormLabel,
  IconButton,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { getWalletTransactions } from "../services/Wallet/walletTransactionService";
import { getPageItems } from "../utils/pagination";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Helper function to format Date objects to YYYY-MM-DD format for API
const formatDateForAPI = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const Transactionstable = ({ walletId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });

  // Date range - using Date objects instead of strings
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const fetchInFlightRef = useRef(null);

  // Helper function to format amount - removes .00 if present
  const formatAmount = (amount) => {
    if (amount == null) return "-";
    const fixed = amount.toFixed(2);
    return fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed;
  };
  
  // Clear date filters
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
  };

  // Fetch transactions
  const fetchTransactions = useCallback(async (page = 1, sortBy = "created_at", sortOrder = "desc") => {
    if (!walletId) return;

    setLoading(true);
    setError(null);

    try {
      const { items: data, pagination } = await getWalletTransactions(
        walletId,
        page,
        itemsPerPage,
        sortBy,
        sortOrder,
        formatDateForAPI(startDate),
        formatDateForAPI(endDate)
      );

      setTransactions(data);
      setTotalPages(Math.ceil(pagination.total / itemsPerPage));
      setTotalItems(pagination.total);
    } catch (err) {
      setError("Failed to load transactions.");
      toast({
        title: "Error",
        description: err.message || "Failed to load transactions.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [walletId, itemsPerPage, startDate, endDate, toast]);

  useEffect(() => {
    if (!walletId) return;
    const requestKey = `${walletId}-${currentPage}-${sortConfig.key}-${sortConfig.direction}-${formatDateForAPI(startDate)}-${formatDateForAPI(endDate)}`;
    if (fetchInFlightRef.current === requestKey) return;
    fetchInFlightRef.current = requestKey;
    fetchTransactions(currentPage, sortConfig.key, sortConfig.direction).finally(() => {
      fetchInFlightRef.current = null;
    });
  }, [walletId, currentPage, sortConfig, startDate, endDate, fetchTransactions]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // 🟢 Export all transactions within selected date range
  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast({
        title: "Select Date Range",
        description: "Please select both start and end dates.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const diffInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    if (diffInDays > 31) {
      toast({
        title: "Invalid Range",
        description: "Date range cannot exceed 31 days.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setExporting(true);
    let allTransactions = [];
    let page = 1;

    try {
      while (true) {
        const { items } = await getWalletTransactions(
          walletId,
          page,
          itemsPerPage,
          sortConfig.key,
          sortConfig.direction,
          formatDateForAPI(startDate),
          formatDateForAPI(endDate)
        );

        allTransactions = [...allTransactions, ...items];
        if (items.length < itemsPerPage) break;
        page++;
      }

      if (allTransactions.length === 0) {
        toast({
          title: "No Data",
          description: "No transactions found in this date range.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(
        allTransactions.map((t) => ({
          "Transaction ID": t.transaction_no,
          "Payment ID": t.payment_id,
          "Amount": formatAmount(t?.amount),
          "Balance": formatAmount(t?.balance),
          "Type": t.amount_type,
          "Payment Mode": t.payment_mode,
          "Description": t.description,
          "Created At": formatDate(t.created_at),
        }))
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

      XLSX.writeFile(
        workbook,
        `transactions_${formatDateForAPI(startDate)}_to_${formatDateForAPI(endDate)}.xlsx`
      );

      toast({
        title: "Export Successful",
        description: `Exported ${allTransactions.length} transactions.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <Alert status="error" mb={4}>
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  // Custom DatePicker styling to match Chakra UI
  const CustomDatePickerInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      ref={ref}
      w="100%"
      px="12px"
      py="6px"
      fontSize="sm"
      border="1px solid"
      borderColor="gray.300"
      borderRadius="md"
      bg="white"
      textAlign="left"
      color={value ? "gray.800" : "gray.400"}
      _hover={{ borderColor: "gray.400" }}
      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
      transition="all 0.2s"
    >
      {value || placeholder}
    </Box>
  ));

  return (
    <>
      {/* Header + Export section */}
      <div className="flex-col gap-2 mb-4 md:flex md:flex-row justify-between">
        <Text className="font-semibold text-[20px]">Transactions</Text>

        <div className="flex flex-col md:flex-row gap-2 items-end">
          {/* Start Date Field */}
          <Box w="100%">
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              Start Date
            </FormLabel>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate || new Date()}
              placeholderText="Select start date"
              dateFormat="dd/MM/yyyy"
              customInput={<CustomDatePickerInput />}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
            />
          </Box>

          {/* End Date Field */}
          <Box w="100%">
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              End Date
            </FormLabel>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              placeholderText="Select end date"
              dateFormat="dd/MM/yyyy"
              customInput={<CustomDatePickerInput />}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
            />
          </Box>

          {/* Clear Filters Button - Only show when dates are selected */}
          {(startDate || endDate) && (
            <Tooltip label="Clear date filters" hasArrow>
              <IconButton
                icon={<CloseIcon />}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={handleClearFilters}
                aria-label="Clear filters"
              />
            </Tooltip>
          )}

          {/* Export Button */}
          <Button
            size="sm"
            w="100%"
            colorScheme="blue"
            onClick={handleExport}
            isLoading={exporting}
          >
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <TableContainer>
        <Table variant="outline" size="md">
          <Thead>
            <Tr bg="#F9FAFB" className="font-semibold text-black">
              <Th onClick={() => handleSort("amount_type")} cursor="pointer">
                Type {sortConfig.key === "amount_type" && (sortConfig.direction === "asc" ? "▲" : "▼")}
              </Th>
              <Th onClick={() => handleSort("created_at")} cursor="pointer">
                Date {sortConfig.key === "created_at" && (sortConfig.direction === "asc" ? "▲" : "▼")}
              </Th>
              <Th>Transaction ID</Th>
              <Th>Payment ID</Th>
              <Th onClick={() => handleSort("amount")} cursor="pointer">
                Amount {sortConfig.key === "amount" && (sortConfig.direction === "asc" ? "▲" : "▼")}
              </Th>
              <Th>Balance</Th>
              <Th>Payment Mode</Th>
              <Th>Description</Th>
            </Tr>
          </Thead>
          <Tbody className="text-sm">
            {loading ? (
              <Tr>
                <Td colSpan={8} textAlign="center">
                  <Center my={8}>
                    <Spinner size="lg" />
                  </Center>
                </Td>
              </Tr>
            ) : transactions.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center">
                  <Text py={8} color="gray.500">
                    No transactions found
                  </Text>
                </Td>
              </Tr>
            ) : (
              transactions.map((item) => (
                <Tr key={item.id}>
                  <Td color={item.amount_type === "topup" ? "green.600" : "blue.600"} textTransform="capitalize">
                    {item.amount_type}
                  </Td>
                  <Td>{formatDate(item.created_at)}</Td>
                  <Td>{item.transaction_no}</Td>
                  <Td>{item.payment_id}</Td>
                  <Td color={item.transaction_type === "credit" ? "green.500" : "red.500"}>
                    {item.transaction_type === "credit"
                      ? `+ ₹${formatAmount(item.amount)}`
                      : `- ₹${formatAmount(item.amount)}`}
                  </Td>
                  <Td>{formatAmount(item.balance)}</Td>
                  <Td textTransform="uppercase">
                    {item.payment_mode === "third_party_pg" ? "CD Balance" : item.payment_mode}
                  </Td>
                  <Td>
                    <Tooltip label={item.description} hasArrow>
                      <span>
                        {item.description?.length > 30
                          ? `${item.description.slice(0, 30)}...`
                          : item.description || "-"}
                      </span>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-4 flex-col md:flex md:flex-row justify-between">
          <Text color="gray.600">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} transactions
          </Text>
          <ButtonGroup spacing={2}>
            <Button
              size="sm"
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              isDisabled={currentPage === 1}
            >
              Prev
            </Button>

            {getPageItems(currentPage, totalPages, 1).map((item, idx) =>
              item === "ellipsis" ? (
                <Button key={`e-${idx}`} size="sm" isDisabled variant="ghost">
                  …
                </Button>
              ) : (
                <Button
                  key={item}
                  size="sm"
                  variant={currentPage === item ? "solid" : "outline"}
                  onClick={() => handlePageChange(item)}
                >
                  {item}
                </Button>
              )
            )}

            <Button
              size="sm"
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </ButtonGroup>
        </div>
      )}
    </>
  );
};

export default Transactionstable;
